import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ConfirmDialog } from "../../components/Dialog";
import { ErrorState } from "../../components/ErrorState";
import { ListSkeleton } from "../../components/Loading";
import { Menu, type MenuAction } from "../../components/Menu";
import { Pager } from "../../components/Pager";
import { useToast } from "../../components/Toast";
import { api, type AdminUser, type RegistrationSettings, type UserStatus } from "../../lib/api";
import { formatDate, plural } from "../../lib/format";
import { useAdminMutation, useAdminSettings, useAdminUsers, useDebounced, useMe } from "../../lib/queries";
import { useTitle } from "../../lib/useTitle";

const STATUSES: { key: UserStatus; label: string }[] = [
  { key: "all", label: "Tous" },
  { key: "pending", label: "À valider" },
  { key: "unconfirmed", label: "Non confirmés" },
  { key: "active", label: "Actifs" },
  { key: "admin", label: "Administrateurs" },
];

export function AdminUsers() {
  useTitle("Utilisateurs");
  const [params, setParams] = useSearchParams();
  const status = (STATUSES.find((s) => s.key === params.get("statut"))?.key ?? "all") as UserStatus;
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim());
  const [page, setPage] = useState(1);
  const users = useAdminUsers({ q, status, page });
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const toast = useToast();
  const myEmail = useMe().data?.email;

  const onError = (error: Error) => toast(error.message, "error");
  const update = useAdminMutation(({ id, ...change }: { id: number; approved?: boolean; is_admin?: boolean }) =>
    api.admin.updateUser(id, change),
  );
  const approveAll = useAdminMutation(() => api.admin.approve({ all_pending: true }));
  const remove = useAdminMutation(api.admin.deleteUser);

  const setStatus = (key: UserStatus) => {
    setPage(1);
    setParams(key === "all" ? {} : { statut: key }, { replace: true });
  };

  function actions(user: AdminUser): MenuAction[] {
    const change = (patch: { approved?: boolean; is_admin?: boolean }, done: string) => () =>
      update.mutate({ id: user.id, ...patch }, { onSuccess: () => toast(done), onError });
    const list: MenuAction[] = [];
    if (user.email === myEmail) return list;
    if (!user.is_admin) {
      list.push(
        user.approved
          ? { label: "Suspendre le compte", onSelect: change({ approved: false }, `${user.email} est suspendu.`) }
          : { label: "Valider le compte", onSelect: change({ approved: true }, `${user.email} est validé.`) },
      );
    }
    list.push(
      user.is_admin
        ? { label: "Retirer les droits d'administration", onSelect: change({ is_admin: false }, `${user.email} n'est plus administrateur.`) }
        : { label: "Rendre administrateur", onSelect: change({ is_admin: true }, `${user.email} est administrateur.`) },
      { label: "Supprimer le compte", danger: true, onSelect: () => setDeleting(user) },
    );
    return list;
  }

  const data = users.data;
  return (
    <div className="admin-users">
      <RegistrationPanel />

      <section aria-labelledby="users-title">
        <div className="admin-head">
          <h2 id="users-title">Comptes</h2>
          {data && data.pending_approval > 0 && (
            <button type="button" className="button button--small" onClick={() => setApprovingAll(true)}>
              Valider les {plural(data.pending_approval, "compte en attente", "comptes en attente")}
            </button>
          )}
        </div>
        <div className="toolbar">
          <div className="segmented" role="group" aria-label="Filtrer les comptes">
            {STATUSES.map((s) => (
              <button key={s.key} type="button" aria-pressed={status === s.key} onClick={() => setStatus(s.key)}>
                {s.label}
                {s.key === "pending" && data ? <span className="count"> {data.pending_approval}</span> : null}
              </button>
            ))}
          </div>
          <input
            className="input"
            type="search"
            aria-label="Rechercher une adresse"
            placeholder="Rechercher une adresse"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {users.isError ? (
          <ErrorState error={users.error} onRetry={() => users.refetch()} />
        ) : !data ? (
          <ListSkeleton />
        ) : data.items.length === 0 ? (
          <p className="empty">Aucun compte ne correspond.</p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="data-table admin-table">
                <thead>
                  <tr>
                    <th scope="col">Adresse</th>
                    <th scope="col">Statut</th>
                    <th scope="col">Inscription</th>
                    <th scope="col" className="num">
                      Suivis
                    </th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((user) => (
                    <tr key={user.id}>
                      <th scope="row" className="admin-table__main">
                        {user.email}
                        {user.brand && <span className="admin-table__sub">Portail {user.brand}</span>}
                      </th>
                      <td>
                        <UserTags user={user} />
                      </td>
                      <td className="nowrap">{formatDate(user.created_at, "short")}</td>
                      <td className="num">
                        <Link
                          to={`/admin/donnees/watches?where=${encodeURIComponent(JSON.stringify({ subscriber_id: user.id }))}`}
                          aria-label={`${plural(user.watch_count, "surveillance", "surveillances")} de ${user.email}`}
                        >
                          {user.watch_count}
                        </Link>
                      </td>
                      <td className="admin-table__actions">
                        {!user.approved && !user.is_admin && (
                          <button
                            type="button"
                            className="button button--small"
                            disabled={update.isPending}
                            onClick={() =>
                              update.mutate(
                                { id: user.id, approved: true },
                                { onSuccess: () => toast(`${user.email} est validé.`), onError },
                              )
                            }
                          >
                            Valider
                          </button>
                        )}
                        {user.email !== myEmail && <Menu label={`Actions pour ${user.email}`} actions={actions(user)} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
          </>
        )}
      </section>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Supprimer ce compte ?"
        body={
          deleting
            ? `${deleting.email} et ses ${plural(deleting.watch_count, "surveillance", "surveillances")} seront effacés. Cette action est définitive.`
            : ""
        }
        confirmLabel="Supprimer définitivement"
        danger
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(deleting!.id, {
            onSuccess: (r) => {
              toast(r.message);
              setDeleting(null);
            },
            onError,
          })
        }
      />
      <ConfirmDialog
        open={approvingAll}
        onClose={() => setApprovingAll(false)}
        title="Valider tous les comptes en attente ?"
        body="Chaque compte validé dont l'adresse est confirmée reçoit un e-mail, et ses alertes démarrent."
        confirmLabel="Tout valider"
        pending={approveAll.isPending}
        onConfirm={() =>
          approveAll.mutate(undefined, {
            onSuccess: (r) => {
              toast(r.message);
              setApprovingAll(false);
            },
            onError,
          })
        }
      />
    </div>
  );
}

function UserTags({ user }: { user: AdminUser }) {
  return (
    <span className="chips">
      {user.is_admin && <span className="tag tag--medium">Admin</span>}
      {!user.approved && <span className="tag tag--high">À valider</span>}
      {user.confirmed_at ? (
        user.approved && !user.is_admin && <span className="tag tag--ok">Actif</span>
      ) : (
        <span className="tag tag--low">Adresse non confirmée</span>
      )}
    </span>
  );
}

function RegistrationPanel() {
  const settings = useAdminSettings();
  const save = useAdminMutation(api.admin.saveSettings);
  const toast = useToast();
  const value = settings.data;

  const toggle = (patch: Partial<RegistrationSettings>, done: string) => {
    if (!value) return;
    save.mutate({ ...value, ...patch }, { onSuccess: () => toast(done), onError: (e) => toast(e.message, "error") });
  };

  return (
    <section className="sheet admin-switches" aria-labelledby="registration-title">
      <h2 id="registration-title">Inscriptions</h2>
      {!value ? (
        <ListSkeleton rows={2} />
      ) : (
        <>
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={value.registrations_open}
              disabled={save.isPending}
              onChange={(e) =>
                toggle(
                  { registrations_open: e.target.checked },
                  e.target.checked ? "Inscriptions ouvertes." : "Inscriptions fermées.",
                )
              }
            />
            <span>
              <span className="switch__label">Inscriptions ouvertes</span>
              <span className="switch__hint">
                Fermées, seules les adresses qui ont déjà un compte peuvent se connecter et suivre des entreprises.
              </span>
            </span>
          </label>
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={value.approval_required}
              disabled={save.isPending}
              onChange={(e) =>
                toggle(
                  { approval_required: e.target.checked },
                  e.target.checked ? "Les nouveaux comptes devront être validés." : "Les nouveaux comptes sont actifs dès leur création.",
                )
              }
            />
            <span>
              <span className="switch__label">Valider chaque nouveau compte</span>
              <span className="switch__hint">
                Les nouveaux comptes peuvent confirmer leur adresse mais ne voient rien et ne reçoivent aucune alerte
                avant validation. Les comptes existants ne sont pas concernés.
              </span>
            </span>
          </label>
        </>
      )}
    </section>
  );
}
